import React from "react";
import styled from "styled-components";
import { FlexRow, LoadingIndicator } from "./CommonStyles";
import {
  AccountContext,
  RetryBehavior,
} from "../common/context/AccountContext";
import { COLOR_IMAGE_OVERLAY } from "./colors";

// A map from imageId to dataUrl
const urlToObjectUrlMap = new Map<string, Promise<any>>();
// Count number of mounted LessonImage components.
// Whenever the last LessonImage is unmounted, we can clear the urlToObjectUrlMap.
let countMountedLessonImages = 0;

// Single shared IntersectionObserver for all lazy LessonImage instances.
// Using one observer for N elements is far cheaper than N separate observers.
const lazyCallbacks = new Map<Element, () => void>();
const lazyObserver = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        lazyCallbacks.get(entry.target)?.();
        lazyObserver.unobserve(entry.target);
        lazyCallbacks.delete(entry.target);
      }
    });
  },
  { rootMargin: "200px" }
);

// Default styling in class so that consumers can override it two ways:
// with an inline `style` prop (wins outright), or with a wrapper `& img {}`
// rule (outranks this class).
const StyledImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

interface Props {
  url: string;
  thumbSize: string;
  alt?: string;
  style?: React.CSSProperties;
  lazy?: boolean;
}
export const LessonImage = (props: Props) => {
  const { makeRequest } = React.useContext(AccountContext);

  // When lazy=true, defer loading until the placeholder enters the viewport.
  const [hasBeenVisible, setHasBeenVisible] = React.useState(!props.lazy);
  const observerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!props.lazy) return;
    const el = observerRef.current;
    if (!el) return;
    lazyCallbacks.set(el, () => setHasBeenVisible(true));
    lazyObserver.observe(el);
    return () => {
      lazyObserver.unobserve(el);
      lazyCallbacks.delete(el);
    };
  }, [props.lazy]);

  // Clear the image ObjectUrl cache whenever the last LessonImage component is unmounted
  React.useEffect(() => {
    countMountedLessonImages += 1;
    return () => {
      // This runs on component unmount.
      countMountedLessonImages -= 1;
      if (countMountedLessonImages === 0) {
        for (let promise of Array.from(urlToObjectUrlMap.values())) {
          promise.then((url) => URL.revokeObjectURL(url));
        }
        urlToObjectUrlMap.clear();
      }
    };
  }, []);

  const [objectUrl, setObjectUrl] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const mostRecentPropsRef = React.useRef<Props>(props);

  const arePropsTheSame = React.useCallback(() => {
    return (
      mostRecentPropsRef.current.url === props.url &&
      mostRecentPropsRef.current.thumbSize === props.thumbSize
    );
  }, [props]);

  React.useEffect(() => {
    if (!hasBeenVisible) return;
    setIsLoading(true);
    mostRecentPropsRef.current = props;

    let urlWithSize =
      props.url +
      (props.url.includes("?") ? "&" : "?") +
      "fmt=" +
      props.thumbSize;
    if (urlToObjectUrlMap.has(urlWithSize)) {
      urlToObjectUrlMap.get(urlWithSize)!.then((result) => {
        if (arePropsTheSame()) {
          setObjectUrl(result);
          setIsLoading(false);
        }
      });
    } else {
      const promise = makeRequest(
        urlWithSize,
        "GET",
        RetryBehavior.EXPOSE_ALL,
        true,
        undefined,
        {
          cache: "default",
        }
      )
        .then(async (response) => {
          if (response.headers.has("x-single-frame-preview")) {
            // If the single frame header exists make another request to
            // get the full size version of the preview after processing the single
            // frame version
            await response.blob().then((blob) => {
              const objectUrl = URL.createObjectURL(blob);
              if (arePropsTheSame()) {
                setObjectUrl(objectUrl);
              }
            });

            urlWithSize += `&force=True`;

            return await makeRequest(
              urlWithSize,
              "GET",
              RetryBehavior.EXPOSE_ALL,
              true,
              undefined,
              {
                cache: "default",
              }
            )
              .then((response) => {
                return response.blob().then((blob) => {
                  const objectUrl = URL.createObjectURL(blob);
                  if (arePropsTheSame()) {
                    setObjectUrl(objectUrl);
                    setIsLoading(false);
                  }
                  return objectUrl;
                });
              })
              .catch(() => {});
          } else {
            return await response.blob().then((blob) => {
              const objectUrl = URL.createObjectURL(blob);
              if (arePropsTheSame()) {
                setObjectUrl(objectUrl);
                setIsLoading(false);
              }
              return objectUrl;
            });
          }
        })
        .catch(() => {});

      urlToObjectUrlMap.set(urlWithSize, promise);
    }
  }, [
    props,
    props.url,
    props.thumbSize,
    arePropsTheSame,
    makeRequest,
    hasBeenVisible,
  ]);

  if (!objectUrl) {
    if (props.lazy && !hasBeenVisible) {
      return <div ref={observerRef} style={{ minWidth: 1, minHeight: 1 }} />;
    }
    return null;
  }

  return (
    <>
      {isLoading && (
        <FlexRow
          style={{
            height: "100%",
            width: "100%",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: COLOR_IMAGE_OVERLAY,
            position: "absolute",
          }}
        >
          <LoadingIndicator size="4x" />
        </FlexRow>
      )}
      <StyledImg src={objectUrl} style={props.style} alt={props.alt} />
    </>
  );
};
